import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, RefreshCw, Settings, AlertCircle, Shield, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function AWSIntegrationPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [region, setRegion] = useState('us-east-1');
  const [awsAccountId, setAwsAccountId] = useState(null);
  const [securityData, setSecurityData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    checkAWSStatus();
  }, [viewingOrg]);

  const checkAWSStatus = async () => {
    try {
      // Add view_tenant_id param if super admin is viewing a tenant
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/aws/status${params}`);
      
      setConfigured(response.data.configured || false);
      setConnected(response.data.connected || false);
      setRegion(response.data.region || 'us-east-1');
      setAwsAccountId(response.data.aws_account_id);
      setLastSync(response.data.last_sync);
      
      if (response.data.connected) {
        fetchSecurityHub();
      } else {
        setSecurityData(null);
      }
    } catch (error) {
      console.error('Failed to check AWS status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSecurityHub = async () => {
    try {
      // Add view_tenant_id param if super admin is viewing a tenant
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/aws/security-hub${params}`);
      setSecurityData(response.data);
    } catch (error) {
      console.error('Failed to fetch Security Hub data:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      }
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/aws/sync-controls`);
      toast.success(`Successfully synced! Updated ${response.data.controls_updated} controls.`);
      fetchSecurityHub();
      checkAWSStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync controls');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect AWS?')) {
      return;
    }
    try {
      await axios.delete(`${API}/aws/disconnect`);
      toast.success('AWS disconnected');
      setConnected(false);
      setConfigured(false);
      setSecurityData(null);
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            AWS Security Hub
          </h1>
          <p className="text-slate-600 mt-1">
            Security findings, compliance status, and recommendations from AWS
          </p>
        </div>
        <div className="flex gap-2">
          {configured && (
            <>
              <Button
                onClick={() => window.location.href = '/aws-settings'}
                variant="outline"
                className="rounded-sm"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button
                onClick={handleSync}
                disabled={syncing || !connected}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Connection Status Banner */}
      {!configured && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-6 text-center">
          <Shield className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Connect Your AWS Account</h3>
          <p className="text-slate-600 mb-4">
            Configure AWS credentials to fetch Security Hub findings and sync compliance controls
          </p>
          <Button
            onClick={() => window.location.href = '/aws-settings'}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            Configure AWS
          </Button>
        </div>
      )}

      {connected && securityData && (
        <>
          {/* Connected Account Info */}
          {awsAccountId && (
            <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FF9900] rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">AWS</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-slate-900">Connected to AWS Security Hub</span>
                  </div>
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Account ID:</span> {awsAccountId} · <span className="font-medium">Region:</span> {region}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Score Overview */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">Secure Score</p>
              <p className={`text-3xl font-heading font-semibold ${
                securityData.secure_score >= 80 ? 'text-green-600' : 
                securityData.secure_score >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {securityData.secure_score}%
              </p>
            </div>
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">Total Findings</p>
              <p className="text-3xl font-heading font-semibold text-slate-900">
                {securityData.total_findings || 0}
              </p>
            </div>
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">Passed</p>
              <p className="text-3xl font-heading font-semibold text-green-600">
                {securityData.passed || 0}
              </p>
            </div>
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">Failed</p>
              <p className="text-3xl font-heading font-semibold text-red-600">
                {securityData.failed || 0}
              </p>
            </div>
            <div className="bg-white border border-border rounded-sm p-4">
              <p className="text-sm font-medium text-slate-600 mb-1">Account</p>
              <p className="text-lg font-mono text-slate-700 truncate">
                {awsAccountId || 'N/A'}
              </p>
              <p className="text-xs text-slate-500">{region}</p>
            </div>
          </div>

          {/* Severity Breakdown */}
          <div className="bg-white border border-border rounded-sm p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Findings by Severity</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-3 bg-red-50 rounded-sm border border-red-100">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700">Critical</span>
                </div>
                <p className="text-2xl font-heading font-semibold text-red-600 mt-1">
                  {securityData.critical || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-50 rounded-sm border border-orange-100">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm text-orange-700">High</span>
                </div>
                <p className="text-2xl font-heading font-semibold text-orange-600 mt-1">
                  {securityData.high || 0}
                </p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-sm border border-yellow-100">
                <span className="text-sm text-yellow-700">Medium</span>
                <p className="text-2xl font-heading font-semibold text-yellow-600 mt-1">
                  {securityData.medium || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-sm border border-blue-100">
                <span className="text-sm text-blue-700">Low</span>
                <p className="text-2xl font-heading font-semibold text-blue-600 mt-1">
                  {securityData.low || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border">
            <nav className="flex gap-4">
              <button
                onClick={() => setActiveTab('overview')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overview' 
                    ? 'border-slate-900 text-slate-900' 
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Non-Compliant Findings
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'all' 
                    ? 'border-slate-900 text-slate-900' 
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                All Findings
              </button>
            </nav>
          </div>

          {/* Non-Compliant Findings with Recommendations */}
          {activeTab === 'overview' && securityData.non_compliant_findings && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-900">
                Non-Compliant Findings ({securityData.non_compliant_findings.length})
              </h3>
              {securityData.non_compliant_findings.length === 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-800 font-medium">All controls are compliant!</p>
                </div>
              ) : (
                securityData.non_compliant_findings.map((finding, index) => (
                  <div key={index} className="bg-white border border-border rounded-sm p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-medium border ${getSeverityColor(finding.severity)}`}>
                            {finding.severity}
                          </span>
                          {finding.standard && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded-sm">
                              {finding.standard}
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium text-slate-900 mb-1">{finding.title}</h4>
                        {finding.description && (
                          <p className="text-sm text-slate-600 mb-2">{finding.description.substring(0, 200)}...</p>
                        )}
                        <div className="text-xs text-slate-500 space-y-1">
                          <p><strong>Resource:</strong> {finding.resource_type} - {finding.resource_id}</p>
                          <p><strong>Region:</strong> {finding.region}</p>
                        </div>
                        
                        {/* Recommendation */}
                        {finding.recommendation && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-sm">
                            <p className="text-xs font-semibold text-blue-800 mb-1">Recommendation:</p>
                            <p className="text-sm text-blue-900">{finding.recommendation}</p>
                            {finding.recommendation_url && (
                              <a 
                                href={finding.recommendation_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                              >
                                Learn more <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* All Findings */}
          {activeTab === 'all' && securityData.findings && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-slate-900">
                All Findings ({securityData.findings.length})
              </h3>
              <div className="bg-white border border-border rounded-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-border">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-600 px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-slate-600 px-4 py-3">Severity</th>
                      <th className="text-left text-xs font-medium text-slate-600 px-4 py-3">Title</th>
                      <th className="text-left text-xs font-medium text-slate-600 px-4 py-3">Resource</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {securityData.findings.map((finding, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${
                            finding.status === 'PASSED' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {finding.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-sm border ${getSeverityColor(finding.severity)}`}>
                            {finding.severity}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-slate-900 truncate max-w-md">{finding.title}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs text-slate-600">{finding.resource_type}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Not Connected State */}
      {configured && !connected && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Connection Not Verified</p>
              <p className="text-sm text-amber-800">
                Credentials saved but not yet verified. Go to Settings and click Test Connection.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
